/**
 * Helpers to control Masonry layout with various image and screen sizes.
 */
import {
    getImageAspectRatio,
    getViewportResolution,
    getCacheBustParam
} from '@/helpers/image.js';


// wide items should take more screen space, this number is the aspect ratio
// 1.5 == image width should be over 150% of its height
const ratioMasonryWidth2 = 1.5;
const ratioMasonryWidth3 = 2.5;
const ratioMasonryWidth4 = 3.5;
const ratioMasonryWidth5 = 5.0;


// for caching purposes, CanvasX is set to pre-defined resolutions
const xResolutions = [
    120, 280, 500, 800, 1200, 1800
];
// also CanvasY resolutions
const yResolutions = [
    120, 280, 320, 640, 800, 1200, 1500, 1800
];


/**
 * Get Width, Height for masonry tiles
 */
export const getMasonryWidthHeight = function (resolution, item) {
    if (!resolution) {
        resolution = {};
    }

    let width = resolution.x ? resolution.x : 'inherit';
    let height = resolution.y ? resolution.y : 'auto';


    // parse "vh" and "vw" from resolution string
    // string value can be anything, except "vw", "vh" are parsed for numeric value
    if (typeof resolution.x === 'string') {
        const vp = getViewportResolution(resolution.x);
        if (vp.height) {
            width = vp.height;
        }
        else if (vp.width) {
            // add some leeway for margin to upscale image res
            width = vp.width + 90;
        }
    }
    if (typeof resolution.y === 'string') {
        const vp = getViewportResolution(resolution.y);
        if (vp.height) {
            height = vp.height;
        }
        else if (vp.width) {
            height = vp.width;
        }
    }


    // calculate ratio from original item ImageInfo (optional)
    const ratio = getImageAspectRatio(item);

    // masonry tiles can take width--2 (or width--3 etc)
    // resolution needs to be 2x (etc)
    // max width should not exceed client display resolution
    if (ratio) {
        const margin = 20; // allow for margin (1em = 16px)
        const screenWidth = (process.client && window.innerWidth) || 1024;
        if (typeof width === 'number') {
            if (ratio > ratioMasonryWidth5 && screenWidth > width * 6) {
                width = (width * 5) + (margin * 4);
            }
            else if (ratio > ratioMasonryWidth4 && screenWidth > width * 5) {
                width = (width * 4) + (margin * 3);
            }
            else if (ratio > ratioMasonryWidth3 && screenWidth > width * 4) {
                width = (width * 3) + (margin * 2);
            }
            else if (ratio > ratioMasonryWidth2 && screenWidth > ((width * 2) + margin)) {
                width = (width * 2) + margin;
            }
        }

        // maximum width depends on screen width (minus page margin),
        // then height must be adjust according to ratio
        if (width > (screenWidth - 90)) {
            width = screenWidth - 90;
            height = width / ratio;
        }
        else if (typeof height === 'number') {
            if (ratio > ratioMasonryWidth5 && screenWidth > width * 6) {
                height = (height * 5) + (margin * 4);
            }
            else if (ratio > ratioMasonryWidth4 && screenWidth > width * 5) {
                height = (height * 4) + (margin * 3);
            }
            else if (ratio > ratioMasonryWidth3 && screenWidth > width * 4) {
                height = (height * 3) + (margin * 2);
            }
            else if (ratio > ratioMasonryWidth2 && screenWidth > ((width * 2) + margin)) {
                height = (height * 2) + margin;
            }
        }
        else if (height === 'auto' && typeof width === 'number') {
            // calculate height from ratio
            height = width / ratio;
        }
    }


    // round to integer
    if (typeof width === 'number') {
        width = Math.round(width);
    }
    if (typeof height === 'number') {
        height = Math.round(height);
    }


    return { width, height };
};


export const getMasonryWidthHeightStyle = function (resolution, item) {
    let { width, height } = getMasonryWidthHeight(resolution, item);

    if (typeof width === 'number') {
        width = width + 'px';
    }
    if (typeof height === 'number') {
        height = height + 'px';
    }

    return `width: ${width}; height: ${height};`;
};


export const getResolutionStep = function (resolutionSteps, val) {
    return _.find(resolutionSteps, (top) => {
        if (val <= top) {
            return top;
        }
    }) || _.last(resolutionSteps);
};


export const getCanvasXStep = function (x) {
    return getResolutionStep(xResolutions, x);
};


export const getCanvasYStep = function (y) {
    return getResolutionStep(yResolutions, y);
};


export const getMasonryCanvasResolution = function (_resolution, viewerZoom, ratio) {
    // set default X resolution, if neither is given
    let resolution;
    if (_resolution && _resolution.y) {
        resolution = _.extend({}, _resolution);
    }
    else {
        resolution = _.extend({x: 500}, _resolution);
    }

    // if viewerZoom (zoom on the client) is given,
    // increase the requested resolution
    if (viewerZoom && resolution.x) {
        resolution.x *= viewerZoom;
    }
    else if (viewerZoom && resolution.y) {
        resolution.y *= viewerZoom;
    }

    const screenWidth = (process.client && window.innerWidth) || 1024;

    // Parse "vh" and "vw" from resolution string.
    // String value can be anything, except "vw", "vh" are parsed for numeric value.
    // resolution.x and .y are modified in place from string to number.
    if (typeof resolution.x === 'string') {
        const vp = getViewportResolution(resolution.x);
        if (vp.height) {
            resolution.x = vp.height;
        }
        else if (vp.width) {
            resolution.x = vp.width;

            // given that "50vw" may in mobile view show the image screen-wide
            if (screenWidth < 576 && resolution.x < 500) {
                resolution.x = 500;
            }
            else if (screenWidth < 769 && resolution.x < 800) {
                resolution.x = 800;
            }
            else {
                // add some leeway for margin to upscale image resolution
                resolution.x += 90;
            }
        }
    }
    if (typeof resolution.y === 'string') {
        const vp = getViewportResolution(resolution.y);
        if (vp.height) {
            resolution.y = vp.height;
        }
        else if (vp.width) {
            resolution.y = vp.width;
        }
        // Y can be a percentage, but it will be accounted for after X resolution step is calculated
    }


    // adjust for Masonry width increase
    if (ratio) {
        if (ratio > ratioMasonryWidth5) {
            resolution.x *= 5;
        }
        else if (ratio > ratioMasonryWidth4) {
            resolution.x *= 4;
        }
        else if (ratio > ratioMasonryWidth3) {
            resolution.x *= 3;
        }
        else if (ratio > ratioMasonryWidth2) {
            resolution.x *= 2;
        }
    }

    // Set resolution limits --
    // Resolution can be anything, but for cache optimisation,
    // the image is queried from backend in resolution steps.
    // When resolution.fixed is set, no step adjustment is made.
    let x;
    if (resolution.x && !resolution.fixed) {
        x = getCanvasXStep(resolution.x);
    }
    else {
        x = resolution.x;
    }

    let y;
    if (typeof resolution.y === 'number') {
        y = resolution.y;

        // calculate new height
        if (ratio) {
            y = Math.round(x * ratio);
        }
    }
    // Y can be a percentage
    else if (typeof resolution.y === 'string' && resolution.y.match(/\d+%/)) {
        const perc = parseInt(resolution.y.replace('%', ''), 10) / 100;
        if (typeof x === 'number') {
            y = Math.round(x * perc);
        }
    }
    // get Y step, unless resolution.fixed is set
    if (y && !resolution.fixed) {
        y = getCanvasYStep(y);
    }

    return { x, y };
};


export const formatImageSrcParams = function (_resolution, format, zoom, viewerZoom, ratio, urlParams, cacheBust, zoomMode, missingImage) {

    const { x, y } = getMasonryCanvasResolution(_resolution, viewerZoom, ratio);

    // construct image url params
    const params = [];
    if (format) {
        // default format is always jpg
        params.push(`Format=${format}`);
    }
    if (x) {
        params.push(`CanvasX=${x}`);
    }
    if (y) {
        params.push(`CanvasY=${y}`);
    }
    if (zoom) {
        params.push(`Zoom=${zoom}`);
    }
    if (urlParams) {
        params.push(urlParams);
    }
    if (zoomMode) {
        params.push(`ZoomMode=${zoomMode}`);
    }
    if (missingImage) {
        params.push(`MissingImage=${missingImage}`);
    }
    if (cacheBust) {
        params.push(getCacheBustParam(true));
    }
    return _.compact(params).join('&');
};


/**
 * Wide items should have item--width-X css class for Masonry.
 */
export const getMasonryClass = function (item) {
    let klass = 'item';
    const ratio = getImageAspectRatio(item);
    if (ratio) {
        if (ratio > ratioMasonryWidth5) {
            klass += " item--width-5";
        }
        else if (ratio > ratioMasonryWidth4) {
            klass += " item--width-4";
        }
        else if (ratio > ratioMasonryWidth3) {
            klass += " item--width-3";
        }
        else if (ratio > ratioMasonryWidth2) {
            klass += " item--width-2";
        }
    }
    return klass;
};
